import React, { useState, useEffect, useMemo } from 'react';

export interface CountdownProps {
  target: string | null | undefined;
  title?: string | null;
  variant?: 'login' | 'heart' | 'admin';
  className?: string;
}

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isPast: boolean;
  isValid: boolean;
}

function calculateTimeRemaining(targetStr: string | null | undefined): TimeRemaining {
  if (!targetStr) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isPast: false, isValid: false };
  }

  const targetTime = new Date(targetStr).getTime();
  if (isNaN(targetTime)) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isPast: false, isValid: false };
  }

  const now = Date.now();
  const diff = targetTime - now;

  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isPast: true, isValid: true };
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);

  return { days, hours, minutes, seconds, isPast: false, isValid: true };
}

function padZero(num: number, minLength = 2): string {
  return String(num).padStart(minLength, '0');
}

export const Countdown: React.FC<CountdownProps> = ({
  target,
  title,
  variant = 'heart',
  className = '',
}) => {
  const [time, setTime] = useState<TimeRemaining>(() => calculateTimeRemaining(target));

  useEffect(() => {
    setTime(calculateTimeRemaining(target));

    if (!target) return;

    const interval = setInterval(() => {
      setTime(calculateTimeRemaining(target));
    }, 1000);

    return () => clearInterval(interval);
  }, [target]);

  const readableLabel = useMemo(() => {
    if (!time.isValid) return 'Countdown unavailable';
    if (time.isPast) return 'Countdown completed';
    return `${time.days} days, ${time.hours} hours, ${time.minutes} minutes, ${time.seconds} seconds remaining`;
  }, [time]);

  if (!time.isValid) {
    return null;
  }

  const displayTitle = (title || '').trim();

  return (
    <div
      className={`countdown-container countdown-${variant} ${time.isPast ? 'countdown-complete' : ''} ${className}`}
      role="timer"
      aria-label={readableLabel}
      aria-live="polite"
    >
      {displayTitle && (
        <div className="countdown-title-wrap">
          <span className="countdown-title">{displayTitle}</span>
        </div>
      )}

      <div className="countdown-timer-units">
        <div className="countdown-unit">
          <span className="countdown-digits">{padZero(time.days)}</span>
          <span className="countdown-label">Days</span>
        </div>

        <span className="countdown-divider" aria-hidden="true">:</span>

        <div className="countdown-unit">
          <span className="countdown-digits">{padZero(time.hours)}</span>
          <span className="countdown-label">Hours</span>
        </div>

        <span className="countdown-divider" aria-hidden="true">:</span>

        <div className="countdown-unit">
          <span className="countdown-digits">{padZero(time.minutes)}</span>
          <span className="countdown-label">Mins</span>
        </div>

        <span className="countdown-divider" aria-hidden="true">:</span>

        <div className="countdown-unit">
          <span className="countdown-digits">{padZero(time.seconds)}</span>
          <span className="countdown-label">Secs</span>
        </div>
      </div>

      {time.isPast && (
        <div className="countdown-status-text">
          <span>The moment is here</span>
        </div>
      )}
    </div>
  );
};
